import React, { useState, useEffect } from "react";
import {
  Users,
  Search,
  UserPlus,
  Edit2,
  Key,
  ShieldAlert,
  ShieldCheck,
  RefreshCw,
  MoreVertical,
  X,
  Check,
  Lock,
  Phone,
  Store,
  Calendar,
  Monitor,
  IndianRupee,
  Trash2,
  RotateCcw,
  AlertTriangle,
} from "lucide-react";
import { useMasterAdmin } from "../../context/MasterAdminAuthContext";
import { toast } from "sonner";

export default function MasterAdminUsers() {
  const { masterApi } = useMasterAdmin();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Modals state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalUser, setEditModalUser] = useState(null);
  const [passwordModalUser, setPasswordModalUser] = useState(null);
  const [renewModalUser, setRenewModalUser] = useState(null);
  const [removeModalUser, setRemoveModalUser] = useState(null);

  // Form states
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newName, setNewName] = useState("");
  const [newShop, setNewShop] = useState("");
  const [newMobile, setNewMobile] = useState("");

  const [editName, setEditName] = useState("");
  const [editShop, setEditShop] = useState("");
  const [editMobile, setEditMobile] = useState("");
  const [editEmail, setEditEmail] = useState("");

  const [resetPasswordVal, setResetPasswordVal] = useState("");

  const [renewMonths, setRenewMonths] = useState(1);
  const [renewPrice, setRenewPrice] = useState(99);
  const [renewMethod, setRenewMethod] = useState("UPI");

  const [submitting, setSubmitting] = useState(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await masterApi.get("/master-admin/users", {
        params: { query: searchQuery, status: statusFilter },
      });
      setUsers(res.data.users || []);
    } catch (err) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [statusFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers();
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUsername.trim() || !newPassword) return;
    setSubmitting(true);
    try {
      await masterApi.post("/master-admin/users", {
        username: newUsername.trim(),
        password: newPassword,
        name: newName.trim() || newUsername.trim(),
        shop_name: newShop.trim(),
        mobile: newMobile.trim(),
      });
      toast.success(`User ${newUsername} created with 1-Month PrintQ License!`);
      setCreateModalOpen(false);
      setNewUsername("");
      setNewPassword("");
      setNewName("");
      setNewShop("");
      setNewMobile("");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!editModalUser) return;
    setSubmitting(true);
    try {
      await masterApi.put(`/master-admin/users/${editModalUser.id}`, {
        name: editName.trim(),
        shop_name: editShop.trim(),
        mobile: editMobile.trim(),
        email: editEmail.trim(),
      });
      toast.success("User details updated successfully");
      setEditModalUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleBlock = async (user) => {
    const nextStatus = !user.enabled;
    try {
      await masterApi.put(`/master-admin/users/${user.id}`, {
        enabled: nextStatus,
      });
      toast.success(`User ${user.username} is now ${nextStatus ? "Active" : "Blocked"}`);
      fetchUsers();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!passwordModalUser || !resetPasswordVal) return;
    setSubmitting(true);
    try {
      await masterApi.post(`/master-admin/users/${passwordModalUser.id}/reset-password`, {
        new_password: resetPasswordVal,
      });
      toast.success(`Password for ${passwordModalUser.username} has been reset`);
      setPasswordModalUser(null);
      setResetPasswordVal("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRenewMembership = async (e) => {
    e.preventDefault();
    if (!renewModalUser) return;
    setSubmitting(true);
    try {
      const res = await masterApi.post(`/master-admin/memberships/${renewModalUser.id}/renew`, {
        months: parseInt(renewMonths, 10) || 1,
        price: parseFloat(renewPrice) || 99.0,
        payment_method: renewMethod,
      });
      toast.success(res.data.message || "Membership renewed successfully");
      setRenewModalUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to renew membership");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnbindDevice = async (user) => {
    const licenseId = user.license?.license_id;
    if (!licenseId) {
      toast.error("No license found for this user");
      return;
    }
    if (!window.confirm(`Unbind registered PC (${user.license?.machine_name || "Device"}) from user ${user.username}? This will allow the user to bind a new PC on next login.`)) {
      return;
    }
    try {
      await masterApi.post(`/master-admin/licenses/${licenseId}/unbind-device`);
      toast.success(`PC unbound successfully for ${user.username}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to unbind PC");
    }
  };

  const handleRemoveUser = async () => {
    if (!removeModalUser) return;
    setSubmitting(true);
    try {
      const res = await masterApi.post(`/master-admin/users/${removeModalUser.id}/remove`);
      toast.success(res.data?.message || `User ${removeModalUser.username} removed successfully`);
      setRemoveModalUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRestoreUser = async (user) => {
    if (!window.confirm(`Restore user @${user.username}? Account will be reactivated.`)) {
      return;
    }
    try {
      const res = await masterApi.post(`/master-admin/users/${user.id}/restore`);
      toast.success(res.data?.message || `User ${user.username} restored successfully`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to restore user");
    }
  };

  const openEdit = (u) => {
    setEditModalUser(u);
    setEditName(u.name || u.full_name || "");
    setEditShop(u.shop_name || "");
    setEditMobile(u.mobile || "");
    setEditEmail(u.email || "");
  };

  const openRenew = (u) => {
    setRenewModalUser(u);
    setRenewMonths(1);
    setRenewPrice(99);
    setRenewMethod("UPI");
  };

  return (
    <div className="flex flex-col gap-6 max-w-7xl mx-auto pb-10">
      {/* Header & Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-black text-white tracking-tight flex items-center gap-2">
            <span>User Management</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300 font-mono">
              {users.length} accounts
            </span>
          </h1>
          <p className="text-xs text-zinc-400 mt-0.5">
            Create shop user accounts, manage ₹99/mo memberships, reset passwords, bind/unbind PCs, and soft-remove users.
          </p>
        </div>

        <button
          onClick={() => setCreateModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 hover:brightness-110 active:scale-95 text-zinc-950 font-bold text-xs shadow-md shadow-amber-500/20 transition-all cursor-pointer self-start sm:self-auto"
        >
          <UserPlus className="w-4 h-4" />
          <span>Create New User</span>
        </button>
      </div>

      {/* Filter & Search Bar */}
      <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
        <form onSubmit={handleSearch} className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by username, name, shop, phone..."
            className="w-full pl-9 pr-4 py-2 rounded-xl bg-zinc-950/80 border border-zinc-800 focus:border-amber-500/60 text-xs text-white placeholder:text-zinc-500 outline-none"
          />
        </form>

        <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
          <div className="flex items-center bg-zinc-950 rounded-xl p-1 border border-zinc-800 text-xs">
            {["all", "active", "inactive", "removed"].map((st) => (
              <button
                key={st}
                onClick={() => setStatusFilter(st)}
                className={`px-3 py-1 rounded-lg capitalize font-semibold transition-colors ${
                  statusFilter === st
                    ? "bg-amber-500/20 text-amber-300 font-bold"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                {st}
              </button>
            ))}
          </div>

          <button
            onClick={fetchUsers}
            className="p-2 rounded-xl bg-zinc-950 hover:bg-zinc-800 border border-zinc-800 text-zinc-400 hover:text-white transition-colors"
            title="Refresh List"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl overflow-hidden shadow-xl">
        {loading ? (
          <div className="flex items-center justify-center h-48 text-zinc-400">
            <div className="w-6 h-6 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <div className="p-10 text-center text-xs text-zinc-500">
            No user accounts found matching your query.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="bg-zinc-950/60 text-zinc-400 border-b border-zinc-800 font-medium">
                  <th className="py-3 px-4">User & Shop Details</th>
                  <th className="py-3 px-4">Contact</th>
                  <th className="py-3 px-4">Membership (₹99)</th>
                  <th className="py-3 px-4">Bound PC (1 PC = 1 Lic)</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/60">
                {users.map((u) => {
                  const mem = u.membership;
                  const lic = u.license;
                  const isRemoved = u.status === "removed" || u.is_removed === true;
                  const isBlocked = !isRemoved && u.enabled === false;
                  const isExp = mem && new Date(mem.expiry_date) < new Date();
                  const isMasterAdminUser = (u.username || "").toLowerCase() === "masteradmin" || u.role === "master_admin";

                  return (
                    <tr key={u.id || u.username} className={`transition-colors ${isRemoved ? "bg-rose-950/10 opacity-70 hover:bg-rose-950/20" : "hover:bg-zinc-800/30"}`}>
                      {/* User & Shop */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-xl border flex items-center justify-center font-bold shrink-0 ${
                            isRemoved
                              ? "bg-rose-500/10 border-rose-500/30 text-rose-400"
                              : "bg-amber-500/10 border-amber-500/30 text-amber-400"
                          }`}>
                            {u.name ? u.name[0].toUpperCase() : u.username[0].toUpperCase()}
                          </div>
                          <div>
                            <div className="font-bold text-white flex items-center gap-1.5">
                              <span className={isRemoved ? "line-through text-zinc-400" : ""}>{u.name || u.username}</span>
                              <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-zinc-800 text-zinc-400">
                                @{u.username}
                              </span>
                            </div>
                            <div className="text-[11px] text-zinc-400 flex items-center gap-1 mt-0.5">
                              <Store className="w-3 h-3 text-zinc-500" />
                              <span>{u.shop_name || "Digital Seva Kendra"}</span>
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Contact */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-1 text-zinc-300 font-mono">
                          <Phone className="w-3 h-3 text-zinc-500" />
                          <span>{u.mobile || "N/A"}</span>
                        </div>
                      </td>

                      {/* Membership */}
                      <td className="py-3.5 px-4">
                        {mem ? (
                          <div>
                            <div className="font-semibold text-white flex items-center gap-1">
                              <span>{mem.plan_name}</span>
                              <span className="text-[10px] text-emerald-400 font-mono">₹{mem.price}</span>
                            </div>
                            <div className={`text-[11px] font-mono mt-0.5 ${isExp ? "text-rose-400" : "text-amber-400/90"}`}>
                              Exp: {new Date(mem.expiry_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                            </div>
                          </div>
                        ) : (
                          <span className="text-zinc-500">No active plan</span>
                        )}
                      </td>

                      {/* Bound PC */}
                      <td className="py-3.5 px-4">
                        {lic?.machine_name ? (
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 shrink-0">
                              <Monitor className="w-3.5 h-3.5" />
                            </div>
                            <div>
                              <div className="font-bold text-white">{lic.machine_name}</div>
                              <div className="text-[10px] font-mono text-zinc-500">{lic.license_id}</div>
                            </div>
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500 italic">
                            <Monitor className="w-3 h-3 text-zinc-600" />
                            <span>Unbound (Awaiting PC login)</span>
                          </span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="py-3.5 px-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold ${
                            isRemoved
                              ? "bg-rose-500/15 text-rose-300 border border-rose-500/30"
                              : isBlocked
                              ? "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                              : "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${isRemoved ? "bg-rose-400" : isBlocked ? "bg-amber-400" : "bg-emerald-400"}`} />
                          <span>{isRemoved ? "Removed" : isBlocked ? "Blocked" : "Active"}</span>
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {isRemoved ? (
                            /* Restore User Action */
                            <button
                              onClick={() => handleRestoreUser(u)}
                              className="flex items-center gap-1 px-3 py-1 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-300 font-bold text-[11px] transition-colors"
                              title="Restore User Account"
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                              <span>Restore</span>
                            </button>
                          ) : (
                            /* Standard Actions */
                            <>
                              <button
                                onClick={() => openRenew(u)}
                                className="px-2.5 py-1 rounded-lg bg-amber-400/15 hover:bg-amber-400/25 border border-amber-400/30 text-amber-300 font-bold text-[11px] transition-colors"
                                title="Renew Membership (+1 Month ₹99)"
                              >
                                Renew
                              </button>

                              {lic?.machine_name && (
                                <button
                                  onClick={() => handleUnbindDevice(u)}
                                  className="px-2 py-1 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 text-[11px] transition-colors"
                                  title="Unbind PC"
                                >
                                  Unbind PC
                                </button>
                              )}

                              <button
                                onClick={() => openEdit(u)}
                                className="p-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors"
                                title="Edit User"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>

                              <button
                                onClick={() => {
                                  setPasswordModalUser(u);
                                  setResetPasswordVal("");
                                }}
                                className="p-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors"
                                title="Reset Password"
                              >
                                <Key className="w-3.5 h-3.5" />
                              </button>

                              <button
                                onClick={() => handleToggleBlock(u)}
                                className={`p-1.5 rounded-lg border transition-colors ${
                                  isBlocked
                                    ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20"
                                    : "bg-amber-500/10 border-amber-500/30 text-amber-400 hover:bg-amber-500/20"
                                }`}
                                title={isBlocked ? "Unblock User" : "Block User"}
                              >
                                {isBlocked ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldAlert className="w-3.5 h-3.5" />}
                              </button>

                              {!isMasterAdminUser && (
                                <button
                                  onClick={() => setRemoveModalUser(u)}
                                  className="p-1.5 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-400 transition-colors"
                                  title="Remove User (Soft Delete)"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal: Create User */}
      {createModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl p-6 flex flex-col gap-4 animate-scale-in">
            <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
              <div className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-amber-400" />
                <h3 className="text-base font-bold text-white">Create New Shop User</h3>
              </div>
              <button
                onClick={() => setCreateModalOpen(false)}
                className="w-7 h-7 rounded-lg bg-zinc-800 text-zinc-400 hover:text-white flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleCreateUser} className="flex flex-col gap-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <label className="font-semibold text-zinc-300">Username *</label>
                  <input
                    type="text"
                    required
                    autoFocus
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    placeholder="e.g. JSM001"
                    className="px-3 py-2 rounded-xl bg-zinc-950 border border-zinc-800 text-white outline-none focus:border-amber-500/60 font-mono"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="font-semibold text-zinc-300">Initial Password *</label>
                  <input
                    type="text"
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Password"
                    className="px-3 py-2 rounded-xl bg-zinc-950 border border-zinc-800 text-white outline-none focus:border-amber-500/60 font-mono"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <label className="font-semibold text-zinc-300">Owner Name</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Ramesh Patil"
                  className="px-3 py-2 rounded-xl bg-zinc-950 border border-zinc-800 text-white outline-none focus:border-amber-500/60"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="font-semibold text-zinc-300">Shop / Business Name</label>
                <input
                  type="text"
                  value={newShop}
                  onChange={(e) => setNewShop(e.target.value)}
                  placeholder="e.g. Patil Xerox & CSC Center"
                  className="px-3 py-2 rounded-xl bg-zinc-950 border border-zinc-800 text-white outline-none focus:border-amber-500/60"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="font-semibold text-zinc-300">Mobile Number</label>
                <input
                  type="text"
                  value={newMobile}
                  onChange={(e) => setNewMobile(e.target.value)}
                  placeholder="e.g. 9876543210"
                  className="px-3 py-2 rounded-xl bg-zinc-950 border border-zinc-800 text-white outline-none focus:border-amber-500/60 font-mono"
                />
              </div>

              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 text-[11px] text-amber-300 flex items-center gap-2">
                <Check className="w-4 h-4 shrink-0" />
                <span>Auto-provisions 1-Month PrintQ License (₹99) and unassigned 1-PC hardware binding.</span>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setCreateModalOpen(false)}
                  className="px-4 py-2 rounded-xl bg-zinc-800 text-zinc-300 font-semibold hover:bg-zinc-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 text-zinc-950 font-bold hover:brightness-110 shadow-md shadow-amber-500/20"
                >
                  {submitting ? "Creating..." : "Create User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Edit User */}
      {editModalUser && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl p-6 flex flex-col gap-4 animate-scale-in">
            <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
              <div className="flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-amber-400" />
                <h3 className="text-base font-bold text-white">Edit User: @{editModalUser.username}</h3>
              </div>
              <button
                onClick={() => setEditModalUser(null)}
                className="w-7 h-7 rounded-lg bg-zinc-800 text-zinc-400 hover:text-white flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleEditUser} className="flex flex-col gap-3 text-xs">
              <div className="flex flex-col gap-1">
                <label className="font-semibold text-zinc-300">Name</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="px-3 py-2 rounded-xl bg-zinc-950 border border-zinc-800 text-white outline-none focus:border-amber-500/60"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="font-semibold text-zinc-300">Shop Name</label>
                <input
                  type="text"
                  value={editShop}
                  onChange={(e) => setEditShop(e.target.value)}
                  className="px-3 py-2 rounded-xl bg-zinc-950 border border-zinc-800 text-white outline-none focus:border-amber-500/60"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <label className="font-semibold text-zinc-300">Mobile</label>
                  <input
                    type="text"
                    value={editMobile}
                    onChange={(e) => setEditMobile(e.target.value)}
                    className="px-3 py-2 rounded-xl bg-zinc-950 border border-zinc-800 text-white outline-none focus:border-amber-500/60 font-mono"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="font-semibold text-zinc-300">Email</label>
                  <input
                    type="email"
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    className="px-3 py-2 rounded-xl bg-zinc-950 border border-zinc-800 text-white outline-none focus:border-amber-500/60"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setEditModalUser(null)}
                  className="px-4 py-2 rounded-xl bg-zinc-800 text-zinc-300 font-semibold hover:bg-zinc-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 text-zinc-950 font-bold hover:brightness-110 shadow-md shadow-amber-500/20"
                >
                  {submitting ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Reset Password */}
      {passwordModalUser && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl p-6 flex flex-col gap-4 animate-scale-in">
            <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
              <div className="flex items-center gap-2">
                <Key className="w-5 h-5 text-amber-400" />
                <h3 className="text-base font-bold text-white">Reset User Password</h3>
              </div>
              <button
                onClick={() => setPasswordModalUser(null)}
                className="w-7 h-7 rounded-lg bg-zinc-800 text-zinc-400 hover:text-white flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleResetPassword} className="flex flex-col gap-3 text-xs">
              <p className="text-zinc-400 leading-relaxed">
                Set a new password for account <span className="font-bold text-white font-mono">{passwordModalUser.username}</span>:
              </p>

              <input
                type="text"
                required
                autoFocus
                value={resetPasswordVal}
                onChange={(e) => setResetPasswordVal(e.target.value)}
                placeholder="Enter new password"
                className="px-3.5 py-2.5 rounded-xl bg-zinc-950 border border-zinc-800 text-white outline-none focus:border-amber-500/60 font-mono text-sm"
              />

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setPasswordModalUser(null)}
                  className="px-4 py-2 rounded-xl bg-zinc-800 text-zinc-300 font-semibold hover:bg-zinc-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !resetPasswordVal}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 text-zinc-950 font-bold hover:brightness-110 shadow-md shadow-amber-500/20"
                >
                  {submitting ? "Resetting..." : "Reset Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Renew Membership */}
      {renewModalUser && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl p-6 flex flex-col gap-4 animate-scale-in">
            <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
              <div className="flex items-center gap-2">
                <IndianRupee className="w-5 h-5 text-amber-400" />
                <h3 className="text-base font-bold text-white">Renew Membership</h3>
              </div>
              <button
                onClick={() => setRenewModalUser(null)}
                className="w-7 h-7 rounded-lg bg-zinc-800 text-zinc-400 hover:text-white flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleRenewMembership} className="flex flex-col gap-3 text-xs">
              <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-3 flex flex-col gap-1">
                <span className="text-zinc-400">User: <strong className="text-white font-mono">{renewModalUser.username}</strong></span>
                <span className="text-zinc-400">Plan: <strong className="text-amber-400">PrintQ Monthly (₹99 / mo)</strong></span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <label className="font-semibold text-zinc-300">Duration (Months)</label>
                  <select
                    value={renewMonths}
                    onChange={(e) => {
                      const m = parseInt(e.target.value, 10);
                      setRenewMonths(m);
                      setRenewPrice(m * 99);
                    }}
                    className="px-3 py-2 rounded-xl bg-zinc-950 border border-zinc-800 text-white outline-none focus:border-amber-500/60"
                  >
                    <option value={1}>1 Month (+30 days)</option>
                    <option value={3}>3 Months (+90 days)</option>
                    <option value={6}>6 Months (+180 days)</option>
                    <option value={12}>1 Year (+365 days)</option>
                  </select>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="font-semibold text-zinc-300">Amount (₹)</label>
                  <input
                    type="number"
                    value={renewPrice}
                    onChange={(e) => setRenewPrice(e.target.value)}
                    className="px-3 py-2 rounded-xl bg-zinc-950 border border-zinc-800 text-white outline-none focus:border-amber-500/60 font-mono"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <label className="font-semibold text-zinc-300">Payment Method</label>
                <select
                  value={renewMethod}
                  onChange={(e) => setRenewMethod(e.target.value)}
                  className="px-3 py-2 rounded-xl bg-zinc-950 border border-zinc-800 text-white outline-none focus:border-amber-500/60"
                >
                  <option value="UPI">UPI / QR Payment</option>
                  <option value="Cash/Manual">Cash / Direct</option>
                  <option value="Bank Transfer">Bank Transfer (NEFT/IMPS)</option>
                </select>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setRenewModalUser(null)}
                  className="px-4 py-2 rounded-xl bg-zinc-800 text-zinc-300 font-semibold hover:bg-zinc-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 text-zinc-950 font-bold hover:brightness-110 shadow-md shadow-amber-500/20"
                >
                  {submitting ? "Processing..." : `Renew for ₹${renewPrice}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Remove User (Soft Delete Confirmation) */}
      {removeModalUser && (
        <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-rose-500/30 rounded-3xl w-full max-w-md overflow-hidden shadow-2xl p-6 flex flex-col gap-4 animate-scale-in">
            <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-rose-500/15 border border-rose-500/30 flex items-center justify-center text-rose-400">
                  <Trash2 className="w-4 h-4" />
                </div>
                <h3 className="text-base font-bold text-white">Remove this PrintQ user?</h3>
              </div>
              <button
                onClick={() => setRemoveModalUser(null)}
                className="w-7 h-7 rounded-lg bg-zinc-800 text-zinc-400 hover:text-white flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* User details card */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-4 flex flex-col gap-2 text-xs">
              <div className="flex justify-between items-center">
                <span className="text-zinc-400">User / Shop:</span>
                <strong className="text-white">{removeModalUser.name || removeModalUser.username}</strong>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-zinc-400">Username:</span>
                <span className="font-mono text-amber-400 font-bold">@{removeModalUser.username}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-zinc-400">Membership:</span>
                <span className="text-emerald-400 font-semibold">{removeModalUser.membership?.plan_name || "PrintQ Monthly"}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-zinc-400">License / PC:</span>
                <span className="text-purple-300 font-mono">{removeModalUser.license?.machine_name || "Unbound"}</span>
              </div>
            </div>

            {/* Warning callout */}
            <div className="bg-rose-500/10 border border-rose-500/20 rounded-2xl p-3.5 flex items-start gap-2.5 text-xs text-rose-300 leading-relaxed">
              <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
              <span>
                This will deactivate the user's PrintQ access and release the assigned license/device. This action can be reversed by Master Admin.
              </span>
            </div>

            {/* Action buttons */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setRemoveModalUser(null)}
                className="px-4 py-2.5 rounded-xl bg-zinc-800 text-zinc-300 font-semibold hover:bg-zinc-700 text-xs transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={handleRemoveUser}
                className="px-5 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-500 active:scale-95 text-white font-bold text-xs shadow-lg shadow-rose-600/30 transition-all cursor-pointer"
              >
                {submitting ? "Removing..." : "Remove User"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
